/**
 * Shop, bench, placement, merging and payout (TRD-07).
 *
 * Every constant is read from the `BalanceSpec` handed in with the run — there are no
 * numbers in this file (TRD-07 AC). A magic number here could not be tuned without a
 * code change and would hide from balance review.
 *
 * Rejected commands are silent no-ops that still count as applied (R-07-08). The client
 * logs every command it dispatched, the server replays the same rejection, and filtering
 * client-side before logging is how the two diverge.
 */
import type { Drone, Enemy, EntityPool, Tower } from '../entities.js';
import type { BenchItem, EconomyState, ShopSlot } from '../economy.js';
import type { SimEvent } from '../events.js';
import type { Rng } from '../rng.js';
import type { BalanceSpec, TowerSpec, TowerTable, TowerTier } from '../scenario.js';
import type { RunMods } from '../mods.js';
import type { EntityId, StarLevel, TowerId } from '../types.js';

export const MAX_STAR: StarLevel = 3;

/** R-07-19 — merges cascade; the bound is a safety net, not an expected limit. */
const MAX_MERGE_ITERATIONS = 10;

export function towerCostOf(balance: BalanceSpec, tier: TowerTier): number {
  return balance.TOWER_COST[tier];
}

/** R-07-06 — 2, 3, 4, 5… within a round, reset on wave clear. */
export function rerollCost(balance: BalanceSpec, rerollsThisRound: number, mods: RunMods): number {
  const raw = balance.REROLL_BASE_COST + rerollsThisRound * balance.REROLL_ESCALATION;
  return Math.ceil(raw * mods.rerollCostMultiplier);
}

/**
 * Roll the shop (R-07-01 … R-07-05).
 *
 * Draws happen in slot order 0→N and each slot takes exactly two draws — tier, then
 * tower. That count and order are part of the replay contract (R-07-04): changing either
 * shifts every subsequent draw in the run and invalidates stored dailies, so treat this
 * function as versioned.
 *
 * Draws are with replacement, deliberately. The same tower appearing twice accelerates a
 * merge and creates a real commit-or-reroll decision.
 */
export function rollShop(
  economy: EconomyState,
  pool: readonly TowerSpec[],
  balance: BalanceSpec,
  wave: number,
  rng: Rng,
): void {
  for (let slot = 0; slot < economy.shop.length; slot++) {
    economy.shop[slot] = rollSlot(pool, balance, wave, rng);
  }
}

function rollSlot(
  pool: readonly TowerSpec[],
  balance: BalanceSpec,
  wave: number,
  rng: Rng,
): ShopSlot | null {
  if (pool.length === 0) return null;

  const row =
    balance.SHOP_TIER_WEIGHTS.find((r) => wave <= r.maxWave) ??
    balance.SHOP_TIER_WEIGHTS[balance.SHOP_TIER_WEIGHTS.length - 1];
  if (!row) return null;

  const tiers: TowerTier[] = [1, 2, 3];
  let tier = rng.weightedPick(tiers, row.weights);

  // R-07-05 — fall back down the tiers rather than leaving a slot empty. An empty slot
  // is a dead shop position the player paid a reroll for.
  let candidates = pool.filter((t) => t.tier === tier);
  while (candidates.length === 0 && tier > 1) {
    tier = (tier - 1) as TowerTier;
    candidates = pool.filter((t) => t.tier === tier);
  }
  if (candidates.length === 0) candidates = pool.slice();

  const picked = rng.pick(candidates);
  return { defId: picked.id, cost: towerCostOf(balance, picked.tier) };
}

export function firstEmptyBenchSlot(economy: EconomyState): number {
  return economy.bench.findIndex((item) => item === null);
}

/** R-07-12 — the one authority on placement legality. The renderer imports this. */
export function canPlace(
  buildMask: readonly string[],
  towers: EntityPool<Tower>,
  tileX: number,
  tileY: number,
): boolean {
  if (!Number.isInteger(tileX) || !Number.isInteger(tileY)) return false;
  const row = buildMask[tileY];
  if (row === undefined) return false;
  if (tileX < 0 || tileX >= row.length) return false;
  if (row[tileX] !== '.') return false;

  const items = towers.items;
  for (let i = 0; i < items.length; i++) {
    const t = items[i] as Tower;
    if (t.alive && t.tileX === tileX && t.tileY === tileY) return false;
  }
  return true;
}

/** R-07-13 — one tile, centred, so range is measured from the middle of the footprint. */
export function applyTowerSpec(tower: Tower, spec: TowerSpec, tileX: number, tileY: number): void {
  tower.defId = spec.id;
  tower.tileX = tileX;
  tower.tileY = tileY;
  tower.x = tileX + 0.5;
  tower.y = tileY + 0.5;
  tower.damage = spec.base.damage;
  tower.damageKind = spec.base.damageKind;
  tower.attackInterval = spec.base.attackInterval;
  tower.range = spec.base.range;
  tower.projectileKind = spec.base.projectile.kind;
  tower.projectileSpeed = spec.base.projectile.speed;
  tower.projectileRadius = spec.base.projectile.radius;
  tower.turnRate = spec.base.projectile.turnRate;
  tower.aoeRadius = spec.base.projectile.aoeRadius;
  // Identity defaults: 1 projectile, no spread, no burst, no pierce, no acceleration,
  // no ramp. A spec that omits them describes exactly the tower it always described.
  const proj = spec.base.projectile;
  tower.salvo = proj.salvo ?? 1;
  tower.spreadRadians = proj.spreadRadians ?? 0;
  tower.burstCount = proj.burstCount ?? 1;
  tower.burstIntervalTicks = proj.burstIntervalTicks ?? 0;
  tower.pierce = proj.pierce ?? 0;
  tower.projectileAcceleration = proj.acceleration ?? 0;
  tower.beamRampPerTick = proj.beamRampPerTick ?? 0;
  tower.beamRampMax = proj.beamRampMax ?? 1;
  // Zone defaults mirror the content schema's, so a hand-built spec (a test fixture)
  // describes the same weapon the Zod pipeline would produce.
  tower.zoneWindupTicks = proj.zoneWindupTicks ?? 30;
  tower.zoneActiveTicks = proj.zoneActiveTicks ?? 120;
  tower.zoneStrikeEveryTicks = proj.zoneStrikeEveryTicks ?? 20;
  tower.zoneTrackSpeed = proj.zoneTrackSpeed ?? 0;
  tower.targetingMode = spec.base.defaultTargeting;
  tower.starScaling = spec.starScaling;
  tower.rangeScaling = spec.rangeScaling ?? [1, 1, 1];
}

// ── Merging (TRD-07 §7) ───────────────────────────────────────────────────────────────

export interface MergeContext {
  economy: EconomyState;
  towers: EntityPool<Tower>;
  drones: EntityPool<Drone>;
  table: TowerTable;
  balance: BalanceSpec;
  events: SimEvent[];
  allocateId: () => EntityId;
}

interface MergeCandidate {
  defId: TowerId;
  star: StarLevel;
  benchIndices: number[];
  placed: Tower[];
  investedGold: number;
}

/**
 * Automatic merging — BENCH ONLY.
 *
 * Bench duplicates carry no strategic weight: they contribute nothing to class counts
 * until they are placed, so combining them is pure convenience and doing it automatically
 * saves the player busywork.
 *
 * Placed towers are a different matter and never merge on their own. Three towers become
 * one, so a merge REMOVES TWO CLASS-SLOTS from the board — a player sitting on exactly six
 * Frost for tier-6 would silently drop to five and lose the synergy the whole run was
 * built around. Merging trades breadth for depth, and that trade belongs to the player
 * (see `mergeTower`).
 */
export function resolveMerges(ctx: MergeContext): boolean {
  let merged = false;

  for (let iteration = 0; iteration < MAX_MERGE_ITERATIONS; iteration++) {
    const candidate = findMergeSet(ctx, true);
    if (!candidate) break;
    performMerge(ctx, candidate);
    merged = true;
  }

  return merged;
}

/**
 * Manual merge, anchored on a tower the player chose.
 *
 * Consumes two further copies at the same star — bench first, so a board the player
 * deliberately arranged is disturbed as little as possible — and upgrades the anchor in
 * place. Returns false when there are not enough copies, which the caller treats as a
 * rejected no-op like any other invalid command (R-07-08).
 */
export function mergeTower(ctx: MergeContext, towerId: EntityId): boolean {
  const anchor = ctx.towers.get(towerId);
  if (!anchor || anchor.star >= MAX_STAR) return false;

  const benchIndices: number[] = [];
  for (let i = 0; i < ctx.economy.bench.length; i++) {
    const item = ctx.economy.bench[i];
    if (item && item.defId === anchor.defId && item.star === anchor.star) benchIndices.push(i);
  }

  const others: Tower[] = [];
  for (let i = 0; i < ctx.towers.items.length; i++) {
    const t = ctx.towers.items[i] as Tower;
    if (!t.alive || t.id === anchor.id) continue;
    if (t.defId === anchor.defId && t.star === anchor.star) others.push(t);
  }

  const need = ctx.balance.MERGE_COUNT - 1;
  if (benchIndices.length + others.length < need) return false;

  const takeBench = benchIndices.slice(0, need);
  const takePlaced = others.slice(0, Math.max(0, need - takeBench.length));

  performMerge(ctx, {
    defId: anchor.defId,
    star: anchor.star,
    benchIndices: takeBench,
    // The anchor goes FIRST so it wins the position — the player picked this tile.
    placed: [anchor, ...takePlaced],
    investedGold: 0,
  });
  return true;
}

/** How many further copies exist for a manual merge — drives the UI's merge affordance. */
export function mergeReadyFor(ctx: MergeContext, tower: Tower): boolean {
  if (tower.star >= MAX_STAR) return false;
  let copies = 0;
  for (const item of ctx.economy.bench) {
    if (item && item.defId === tower.defId && item.star === tower.star) copies++;
  }
  for (const t of ctx.towers.items) {
    if (t.alive && t.id !== tower.id && t.defId === tower.defId && t.star === tower.star) copies++;
  }
  return copies >= ctx.balance.MERGE_COUNT - 1;
}

function findMergeSet(ctx: MergeContext, benchOnly: boolean): MergeCandidate | undefined {
  // Counting first is worth the extra pass: this runs after every buy and every
  // placement, and for most of a run there are nowhere near three mergeable copies
  // about. Bailing here avoids allocating a Map and its candidate arrays on the
  // overwhelming majority of calls, which showed up as ~25% on the replay budget.
  let mergeable = 0;
  for (let i = 0; i < ctx.economy.bench.length; i++) {
    const item = ctx.economy.bench[i];
    if (item && item.star < MAX_STAR) mergeable++;
  }
  if (!benchOnly) {
    for (let i = 0; i < ctx.towers.items.length; i++) {
      const t = ctx.towers.items[i] as Tower;
      if (t.alive && t.star < MAX_STAR) mergeable++;
    }
  }
  if (mergeable < ctx.balance.MERGE_COUNT) return undefined;

  const groups = new Map<string, MergeCandidate>();

  // Bench first, then placed, both in index order, so the set chosen for a merge is a
  // deterministic function of state rather than of Map iteration luck.
  for (let i = 0; i < ctx.economy.bench.length; i++) {
    const item = ctx.economy.bench[i];
    if (!item || item.star >= MAX_STAR) continue;
    groupFor(groups, item.defId, item.star).benchIndices.push(i);
    groupFor(groups, item.defId, item.star).investedGold += item.investedGold;
  }

  if (!benchOnly) {
    const items = ctx.towers.items;
    for (let i = 0; i < items.length; i++) {
      const t = items[i] as Tower;
      if (!t.alive || t.star >= MAX_STAR) continue;
      groupFor(groups, t.defId, t.star).placed.push(t);
      groupFor(groups, t.defId, t.star).investedGold += t.investedGold;
    }
  }

  for (const candidate of groups.values()) {
    if (candidate.benchIndices.length + candidate.placed.length >= ctx.balance.MERGE_COUNT) {
      return candidate;
    }
  }
  return undefined;
}

function groupFor(
  groups: Map<string, MergeCandidate>,
  defId: TowerId,
  star: StarLevel,
): MergeCandidate {
  const key = `${defId}#${star}`;
  let entry = groups.get(key);
  if (!entry) {
    entry = { defId, star, benchIndices: [], placed: [], investedGold: 0 };
    groups.set(key, entry);
  }
  return entry;
}

/**
 * Consume three copies and produce one at the next star.
 *
 * Where the result lands follows R-07-18: onto the position of the placed component with
 * the lowest entity id if any were placed, and onto the bench otherwise. Either way it
 * costs no placement — a merge is a reward, and charging the round's budget for it would
 * make players avoid the best thing in the game.
 */
function performMerge(ctx: MergeContext, candidate: MergeCandidate): void {
  const need = ctx.balance.MERGE_COUNT;

  // Prefer consuming bench copies, so a placed tower stays on the board where the player
  // put it whenever there is a choice.
  const takeBench = candidate.benchIndices.slice(0, need);
  const takePlaced = candidate.placed.slice(0, Math.max(0, need - takeBench.length));

  let invested = 0;
  // Damage the components already dealt travels with the merge (playtest request).
  // The result is a NEW entity, so without this the three copies' contribution simply
  // vanished from the end-of-run damage bars — a board that merged well looked like it
  // had done less work than one that never merged at all. Presentation-only, exactly
  // like the counters it sums: never hashed, never read by a rule.
  let dealt = 0;
  let assisted = 0;
  for (const index of takeBench) {
    const item = ctx.economy.bench[index];
    if (item) invested += item.investedGold;
    ctx.economy.bench[index] = null;
  }

  // The FIRST placed component wins the position. For a manual merge that is the tower
  // the player picked; for any other path the list is already in deterministic order.
  const anchor = takePlaced[0];

  for (const tower of takePlaced) {
    invested += tower.investedGold;
    dealt += tower.damageDealt;
    assisted += tower.assistDealt;
    ctx.towers.kill(tower);
    expireDronesOf(ctx, tower.id);
  }

  const nextStar = (candidate.star + 1) as StarLevel;

  if (anchor) {
    const spec = ctx.table[candidate.defId];
    const result = ctx.towers.spawn(ctx.allocateId());
    if (spec) applyTowerSpec(result, spec, anchor.tileX, anchor.tileY);
    else {
      result.defId = candidate.defId;
      result.tileX = anchor.tileX;
      result.tileY = anchor.tileY;
      result.x = anchor.x;
      result.y = anchor.y;
    }
    result.star = nextStar;
    result.investedGold = invested;
    result.damageDealt = dealt;
    result.assistDealt = assisted;
    ctx.events.push({
      t: 'towersMerged',
      result: result.id,
      defId: candidate.defId,
      star: nextStar,
      x: result.x,
      y: result.y,
    });
    return;
  }

  const slot = firstEmptyBenchSlot(ctx.economy);
  const benched: BenchItem = { defId: candidate.defId, star: nextStar, investedGold: invested };
  if (slot >= 0) ctx.economy.bench[slot] = benched;
  else ctx.economy.bench[takeBench[0] ?? 0] = benched;

  ctx.events.push({
    t: 'towersMerged',
    result: -1,
    defId: candidate.defId,
    star: nextStar,
    x: 0,
    y: 0,
  });
}

/** R-06-20 / R-07-23 — a tower leaving the board takes its drones with it. */
export function expireDronesOf(ctx: { drones: EntityPool<Drone> }, towerId: EntityId): void {
  const items = ctx.drones.items;
  for (let i = 0; i < items.length; i++) {
    const d = items[i];
    if (d && d.alive && d.ownerTower === towerId) d.alive = false;
  }
}

// ── Payout (TRD-07 §9) ────────────────────────────────────────────────────────────────

export interface PayoutResult {
  base: number;
  interest: number;
  total: number;
}

/**
 * R-07-25 — interest is computed on gold held BEFORE the payout lands.
 *
 * Computing it afterwards compounds absurdly: the wave's own income would immediately
 * earn interest on itself, and hoarding would beat playing by an ever-widening margin.
 */
export function computePayout(
  goldBefore: number,
  wave: number,
  balance: BalanceSpec,
  mods: RunMods,
): PayoutResult {
  const base = balance.WAVE_PAYOUT_BASE + wave + mods.goldPerWave;
  const cap = mods.interestCap;
  const interest = Math.min(Math.floor(goldBefore * balance.INTEREST_RATE), cap);
  return { base, interest, total: base + interest };
}

/** R-07-23 — 60% of everything sunk in, floored. Never refunds a placement. */
export function sellRefund(investedGold: number, balance: BalanceSpec): number {
  return Math.floor(investedGold * balance.SELL_REFUND_RATE);
}

/** Enemies are irrelevant here; the parameter keeps the call sites uniform. */
export type UnusedEnemies = EntityPool<Enemy>;
